import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Divider,
  useTheme,
  alpha,
  Skeleton,
  FormControlLabel,
  Switch,
  Badge,
  Tooltip,
  Tab,
  Tabs,
  Avatar
} from '@mui/material';
import {
  Group as GroupIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  Diamond as DiamondIcon,
  WorkspacePremium as CrownIcon,
  Shield as ShieldIcon,
  Favorite as HeartIcon,
  Save as SaveIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api, { handleApiError } from '../utils/api';
import CustomFieldsManager from './CustomFieldsManager';

const MemberTypeManager = () => {
  const { user } = useAuth();
  const theme = useTheme();
  
  const [memberTypes, setMemberTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingType, setEditingType] = useState(null);
  
  // Removed unused customFieldData state

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    priority: 10,
    defaultQuotas: {
      dailyLimit: 3,
      weeklyLimit: 15,
      monthlyLimit: 60,
      maxAdvanceBookingDays: 7
    },
    displaySettings: {
      color: '#2196f3',
      icon: 'school',
      badgeText: ''
    },
    accessSettings: {
      canBookFacilities: true,
      canJoinClasses: true,
      canAccessPremiumFacilities: false,
      requiresApproval: false
    },
    pricing: {
      monthlyFee: 0,
      bookingFee: 0,
      currency: 'TRY'
    },
    customFields: [],
    isActive: true,
    isDefault: false
  });

  const iconOptions = [
    { value: 'school', label: 'School', icon: <SchoolIcon /> },
    { value: 'business', label: 'Business', icon: <BusinessIcon /> },
    { value: 'public', label: 'Public', icon: <PublicIcon /> },
    { value: 'star', label: 'Star', icon: <StarIcon /> },
    { value: 'diamond', label: 'Diamond', icon: <DiamondIcon /> },
    { value: 'crown', label: 'Crown', icon: <CrownIcon /> },
    { value: 'shield', label: 'Shield', icon: <ShieldIcon /> },
    { value: 'heart', label: 'Heart', icon: <HeartIcon /> }
  ];

  const colorOptions = [
    '#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', 
    '#00bcd4', '#795548', '#607d8b', '#e91e63', '#3f51b5'
  ];

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchMemberTypes();
    }
  }, [user]);

  const fetchMemberTypes = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/member-types?admin=true');
      setMemberTypes(response.data.data || []);
      
    } catch (error) {
      setError('Failed to load member types');
      console.error('Error fetching member types:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      description: '',
      priority: 10,
      defaultQuotas: {
        dailyLimit: 3,
        weeklyLimit: 15,
        monthlyLimit: 60,
        maxAdvanceBookingDays: 7
      },
      displaySettings: {
        color: '#2196f3',
        icon: 'school',
        badgeText: ''
      },
      accessSettings: {
        canBookFacilities: true,
        canJoinClasses: true,
        canAccessPremiumFacilities: false,
        requiresApproval: false
      },
      pricing: {
        monthlyFee: 0,
        bookingFee: 0,
        currency: 'TRY'
      },
      customFields: [],
      isActive: true,
      isDefault: false
    });
    setEditingType(null);
  };

  const handleEdit = (memberType) => {
    setFormData({
      name: memberType.name,
      code: memberType.code,
      description: memberType.description || '',
      priority: memberType.priority,
      defaultQuotas: memberType.defaultQuotas || {
        dailyLimit: 3,
        weeklyLimit: 15,
        monthlyLimit: 60,
        maxAdvanceBookingDays: 7
      },
      displaySettings: memberType.displaySettings || {
        color: '#2196f3',
        icon: 'school',
        badgeText: ''
      },
      accessSettings: memberType.accessSettings || {
        canBookFacilities: true,
        canJoinClasses: true,
        canAccessPremiumFacilities: false,
        requiresApproval: false
      },
      pricing: memberType.pricing || {
        monthlyFee: 0,
        bookingFee: 0,
        currency: 'TRY'
      },
      customFields: memberType.customFields || [],
      isActive: memberType.isActive,
      isDefault: memberType.isDefault
    });
    setEditingType(memberType);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      // Generate code from name if not provided
      if (!formData.code && formData.name) {
        formData.code = formData.name.toLowerCase().replace(/[^a-z0-9]/g, '_');
      }

      if (editingType) {
        await api.put(`/member-types/${editingType._id}`, formData);
      } else {
        await api.post('/member-types', formData);
      }

      await fetchMemberTypes();
      setShowForm(false);
      resetForm();
      setSuccess(editingType ? 'Member type updated successfully' : 'Member type created successfully');

    } catch (error) {
      setError(handleApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (memberTypeId) => {
    if (!window.confirm('Are you sure you want to delete this member type?')) {
      return;
    }

    try {
      await api.delete(`/member-types/${memberTypeId}`);
      await fetchMemberTypes();
      setSuccess('Member type deleted successfully');
    } catch (error) {
      setError(handleApiError(error));
    }
  };

  const handleSetDefault = async (memberTypeId) => {
    try {
      await api.put(`/member-types/${memberTypeId}/set-default`);
      await fetchMemberTypes();
      setSuccess('Default member type updated successfully');
    } catch (error) {
      setError(handleApiError(error));
    }
  };

  const handleToggleStatus = async (memberTypeId) => {
    try {
      await api.put(`/member-types/${memberTypeId}/toggle-status`);
      await fetchMemberTypes();
      setSuccess('Member type status updated successfully');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to update member type status');
    }
  };

  const getIconComponent = (iconName) => {
    const iconMap = {
      school: <SchoolIcon />,
      business: <BusinessIcon />,
      public: <PublicIcon />,
      star: <StarIcon />,
      diamond: <DiamondIcon />,
      crown: <CrownIcon />,
      shield: <ShieldIcon />,
      heart: <HeartIcon />
    };
    return iconMap[iconName] || <GroupIcon />;
  };

  if (user?.role !== 'admin') {
    return (
      <Alert severity="warning">
        This feature is only accessible to administrators.
      </Alert>
    );
  }

  function TabPanel({ children, value, index, ...other }) {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`member-type-tabpanel-${index}`}
        aria-labelledby={`member-type-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ 
        mb: 4,
        background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
        borderRadius: 3,
        p: 4,
        position: 'relative',
        overflow: 'hidden'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <GroupIcon sx={{ fontSize: 48, mr: 2, color: 'success.main' }} />
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5, color: 'success.main' }}>
                Member Type Management
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400 }}>
                Configure flexible member categories with custom quotas and access levels
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            sx={{ borderRadius: 2 }}
          >
            Add Member Type
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Member Types" icon={<GroupIcon />} />
          <Tab label="Statistics" icon={<AnalyticsIcon />} />
        </Tabs>
      </Box>

      {/* Member Types Tab */}
      <TabPanel value={tabValue} index={0}>
        {loading ? (
          <Box>
            {[1, 2, 3].map(i => (
              <Card key={i} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
                      <Box sx={{ flex: 1 }}>
                        <Skeleton width="60%" height={24} sx={{ mb: 1 }} />
                        <Skeleton width="40%" height={20} />
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Skeleton width={80} height={32} />
                      <Skeleton width={60} height={32} />
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : memberTypes.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <GroupIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No Member Types Configured
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create member types to categorize users with different access levels and quotas.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setShowForm(true);
              }}
            >
              Create First Member Type
            </Button>
          </Paper>
        ) : (
          <Box>
            {memberTypes.map((memberType) => (
              <Card key={memberType._id} sx={{ mb: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                      <Avatar 
                        sx={{ 
                          bgcolor: memberType.displaySettings?.color || '#2196f3',
                          mr: 2,
                          width: 48,
                          height: 48
                        }}
                      >
                        {getIconComponent(memberType.displaySettings?.icon)}
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6">
                            {memberType.name}
                          </Typography>
                          {memberType.isDefault && (
                            <Chip 
                              label="Default" 
                              size="small" 
                              color="primary" 
                              icon={<StarIcon />}
                            />
                          )}
                          <Chip 
                            label={`Priority ${memberType.priority}`}
                            size="small"
                            variant="outlined"
                          />
                          <Badge badgeContent={memberType.userCount} color="info">
                            <GroupIcon fontSize="small" />
                          </Badge>
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {memberType.description || 'No description'}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip 
                            label={`${memberType.defaultQuotas?.dailyLimit || 0}/day`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip 
                            label={`${memberType.defaultQuotas?.weeklyLimit || 0}/week`}
                            size="small"
                            variant="outlined"
                          />
                          <Chip 
                            label={`${memberType.defaultQuotas?.monthlyLimit || 0}/month`}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={memberType.isActive}
                            onChange={() => handleToggleStatus(memberType._id)}
                            size="small"
                          />
                        }
                        label="Active"
                        labelPlacement="top"
                      />
                      <Tooltip title="Set as Default">
                        <IconButton 
                          size="small"
                          onClick={() => handleSetDefault(memberType._id)}
                          disabled={memberType.isDefault}
                          color={memberType.isDefault ? 'primary' : 'default'}
                        >
                          <StarIcon />
                        </IconButton>
                      </Tooltip>
                      <IconButton size="small" onClick={() => handleEdit(memberType)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleDelete(memberType._id)}
                        disabled={memberType.isDefault || (memberType.userCount || 0) > 0}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </TabPanel>

      {/* Statistics Tab */}
      <TabPanel value={tabValue} index={1}>
        <Typography variant="h6" gutterBottom>Member Type Statistics</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Usage analytics and member distribution across different member types.
        </Typography>
        
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <AnalyticsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            Detailed statistics coming soon
          </Typography>
        </Paper>
      </TabPanel>

      {/* Member Type Form Dialog */}
      <Dialog 
        open={showForm} 
        onClose={() => setShowForm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editingType ? 'Edit Member Type' : 'Add New Member Type'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* Basic Information */}
            <Grid size={12}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid size={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                required
                helperText="Display name for this member type"
              />
            </Grid>

            <Grid size={6}>
              <TextField
                fullWidth
                label="Code"
                value={formData.code}
                onChange={(e) => handleFormChange('code', e.target.value)}
                required
                helperText="Unique identifier (lowercase, numbers, underscores only)"
              />
            </Grid>

            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Description"
                value={formData.description}
                onChange={(e) => handleFormChange('description', e.target.value)}
                helperText="Optional description of this member type"
              />
            </Grid>

            <Grid size={6}>
              <TextField
                fullWidth
                type="number"
                label="Priority"
                value={formData.priority}
                onChange={(e) => handleFormChange('priority', parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 100 }}
                helperText="1 = highest priority, 100 = lowest priority"
              />
            </Grid>

            {/* Display Settings */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Display Settings
              </Typography>
            </Grid>

            <Grid size={6}>
              <FormControl fullWidth>
                <InputLabel>Icon</InputLabel>
                <Select
                  value={formData.displaySettings?.icon || 'school'}
                  label="Icon"
                  onChange={(e) => handleFormChange('displaySettings.icon', e.target.value)}
                >
                  {iconOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {option.icon}
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={6}>
              <Box>
                <Typography variant="body2" gutterBottom>Color</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {colorOptions.map(color => (
                    <Box
                      key={color}
                      sx={{
                        width: 32,
                        height: 32,
                        backgroundColor: color,
                        borderRadius: 1,
                        cursor: 'pointer',
                        border: formData.displaySettings?.color === color ? '3px solid #000' : '1px solid #ccc'
                      }}
                      onClick={() => handleFormChange('displaySettings.color', color)}
                    />
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* Quota Settings */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Default Quota Limits
              </Typography>
            </Grid>

            <Grid size={3}>
              <TextField
                fullWidth
                type="number"
                label="Daily Limit"
                value={formData.defaultQuotas?.dailyLimit || 0}
                onChange={(e) => handleFormChange('defaultQuotas.dailyLimit', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 50 }}
              />
            </Grid>

            <Grid size={3}>
              <TextField
                fullWidth
                type="number"
                label="Weekly Limit"
                value={formData.defaultQuotas?.weeklyLimit || 0}
                onChange={(e) => handleFormChange('defaultQuotas.weeklyLimit', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 200 }}
              />
            </Grid>

            <Grid size={3}>
              <TextField
                fullWidth
                type="number"
                label="Monthly Limit"
                value={formData.defaultQuotas?.monthlyLimit || 0}
                onChange={(e) => handleFormChange('defaultQuotas.monthlyLimit', parseInt(e.target.value) || 0)}
                inputProps={{ min: 0, max: 500 }}
              />
            </Grid>

            <Grid size={3}>
              <TextField
                fullWidth
                type="number"
                label="Advance Booking Days"
                value={formData.defaultQuotas?.maxAdvanceBookingDays || 1}
                onChange={(e) => handleFormChange('defaultQuotas.maxAdvanceBookingDays', parseInt(e.target.value) || 1)}
                inputProps={{ min: 1, max: 90 }}
              />
            </Grid>

            {/* Access Settings */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Access Permissions
              </Typography>
            </Grid>

            <Grid size={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.accessSettings?.canBookFacilities || false}
                    onChange={(e) => handleFormChange('accessSettings.canBookFacilities', e.target.checked)}
                  />
                }
                label="Can Book Facilities"
              />
            </Grid>

            <Grid size={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.accessSettings?.canJoinClasses || false}
                    onChange={(e) => handleFormChange('accessSettings.canJoinClasses', e.target.checked)}
                  />
                }
                label="Can Join Classes"
              />
            </Grid>

            <Grid size={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.accessSettings?.canAccessPremiumFacilities || false}
                    onChange={(e) => handleFormChange('accessSettings.canAccessPremiumFacilities', e.target.checked)}
                  />
                }
                label="Can Access Premium Facilities"
              />
            </Grid>

            <Grid size={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.accessSettings?.requiresApproval || false}
                    onChange={(e) => handleFormChange('accessSettings.requiresApproval', e.target.checked)}
                  />
                }
                label="Requires Booking Approval"
              />
            </Grid>

            {/* Status Settings */}
            <Grid size={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Status Settings
              </Typography>
            </Grid>

            <Grid size={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={(e) => handleFormChange('isActive', e.target.checked)}
                  />
                }
                label="Active"
              />
            </Grid>

            <Grid size={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isDefault}
                    onChange={(e) => handleFormChange('isDefault', e.target.checked)}
                  />
                }
                label="Set as Default"
              />
            </Grid>
            
            {/* Custom Fields Management */}
            <Grid size={12}>
              <CustomFieldsManager 
                customFields={formData.customFields || []}
                onUpdateFields={(fields) => handleFormChange('customFields', fields)}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowForm(false)} disabled={saving}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            disabled={saving || !formData.name || !formData.code}
            startIcon={<SaveIcon />}
          >
            {saving ? 'Saving...' : (editingType ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MemberTypeManager;